import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  helpLink?: string;
}

export default function OnboardingChecklist() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    {
      id: 'create-drill',
      title: 'Create a Drill Design',
      description: 'Start by creating your first drill design',
      completed: false,
      helpLink: 'https://help.drillflo.com/documentation/getting-started'
    },
    {
      id: 'add-performers',
      title: 'Add Performers',
      description: 'Add performers to your drill design',
      completed: false,
      helpLink: 'https://help.drillflo.com/documentation/adding-performers-with-labels-in-drillflo'
    },
    {
      id: 'add-audio',
      title: 'Add Audio',
      description: 'Upload an audio file to your drill',
      completed: false,
      helpLink: 'https://help.drillflo.com/documentation/adding-audio-to-your-drill'
    },
    {
      id: 'export-drill',
      title: 'Export Drill',
      description: 'Export your drill to PDF',
      completed: false,
      helpLink: 'https://help.drillflo.com/documentation/how-to-print-your-drillflo-drill-to-pdf'
    }
  ]);

  useEffect(() => {
    // Check localStorage first
    const checklistHidden = localStorage.getItem('onboardingChecklistHidden');
    if (checklistHidden === 'true') {
      setIsVisible(false);
      return;
    }

    // If not hidden in localStorage, fetch from Firestore
    const fetchChecklistProgress = async () => {
      if (!auth.currentUser) return;

      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const checklist = userData.onboardingChecklist || {};
          
          // Update local state with Firestore data
          setChecklistItems(prevItems => {
            const updatedItems = prevItems.map(item => ({
              ...item,
              completed: checklist[item.id] || false
            }));

            // Check if all items are completed
            const allCompleted = updatedItems.every(item => checklist[item.id]);
            if (allCompleted) {
              // Store in localStorage that the checklist is completed
              localStorage.setItem('onboardingChecklistHidden', 'true');
              setIsVisible(false);
            }

            return updatedItems;
          });
        }
      } catch (error) {
        console.error('Error fetching checklist progress:', error);
      }
    };

    fetchChecklistProgress();
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    // Store in localStorage that the user has dismissed the checklist
    localStorage.setItem('onboardingChecklistHidden', 'true');
  };

  if (!isVisible) return null;

  const completedCount = checklistItems.filter(item => item.completed).length;
  const totalItems = checklistItems.length;

  return (
    <div className="fixed top-20 right-4 z-50 bg-white rounded-xl shadow-xl border border-gray-200 w-92 text-left">
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-900 text-lg">🎉 DrillFlo Getting Started</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-500 hover:text-gray-700 transition"
            aria-label="Toggle checklist"
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-700 transition"
            aria-label="Dismiss onboarding"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
  
      <div className="mb-3">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Progress</span>
          <span>{completedCount}/{totalItems}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-in-out ${
              completedCount === totalItems ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${(completedCount / totalItems) * 100}%` }}
          />
        </div>
        {completedCount === totalItems && (
          <p className="text-green-600 text-sm font-medium mt-2">All steps completed! 🚀</p>
        )}
      </div>
  
      <div
  className={`transition-all duration-500 ease-in-out overflow-hidden`}
  style={{
    maxHeight: isExpanded ? '1000px' : '0px',
    opacity: isExpanded ? 1 : 0,
    pointerEvents: isExpanded ? 'auto' : 'none',
  }}
>
  <div className="space-y-3">
    {checklistItems.map((item) => (
      <div
        key={item.id}
        className={`flex items-start gap-3 p-3 rounded-lg border transition ${
          item.completed ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'
        }`}
      >
        <div className={`mt-1 ${item.completed ? 'text-green-500' : 'text-gray-400'}`}>
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div>
          <h4
            className={`font-medium cursor-pointer hover:underline ${item.completed ? 'text-green-700' : 'text-gray-900'}`}
            onClick={() => window.open(item.helpLink, '_blank')}
          >
            {item.title}
          </h4>
          <p className={`text-sm ${item.completed ? 'text-green-600' : 'text-gray-500'}`}>
            {item.description}
          </p>
        </div>
      </div>
    ))}
  </div>
</div>

    </div>
  </div>
  
  );
} 